
import { useState, useEffect, useCallback } from "react";
import { Group, Product } from "@/lib/types";
import { getGroups, getProducts, fetchGroups, fetchProducts } from "@/lib/storage";
import { ProductQuantity } from "@/components/group/GroupProductQuantities";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

// Changed to named export
export const useUsageTrackerData = () => {
  const [groups, setGroups] = useState<Group[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<boolean>(false);
  const [groupProductQuantities, setGroupProductQuantities] = useState<Record<string, ProductQuantity[]>>({});
  const { toast } = useToast();
  
  // Function to load data with retry logic
  const loadData = useCallback(async (isRetry = false) => {
    setIsLoading(true);
    setError(false);
    
    try {
      console.log(`QuickUsageTracker: ${isRetry ? 'Retrying' : 'Loading'} data...`);
      
      // Create a timeout promise to handle network issues
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Request timeout')), 8000);
      });
      
      // Fetch groups with timeout
      const groupsPromise = fetchGroups(true);
      const fetchedGroups = await Promise.race([groupsPromise, timeoutPromise]) as Group[];
      
      // Fetch products with timeout
      const productsPromise = fetchProducts(true);
      const fetchedProducts = await Promise.race([productsPromise, timeoutPromise]) as Product[];
      
      console.log(`QuickUsageTracker: Loaded ${fetchedGroups.length} groups and ${fetchedProducts.length} products`);
      
      setGroups(fetchedGroups);
      setProducts(fetchedProducts);
      
      // Load saved product quantities for all groups
      if (fetchedGroups.length > 0) {
        try {
          const { data, error } = await supabase
            .from('group_product_quantities')
            .select('group_id, product_id, quantity');
            
          if (error) throw error;
          
          if (data && data.length > 0) {
            const quantitiesByGroup: Record<string, ProductQuantity[]> = {};
            
            data.forEach(item => {
              if (!quantitiesByGroup[item.group_id]) {
                quantitiesByGroup[item.group_id] = [];
              }
              
              quantitiesByGroup[item.group_id].push({
                productId: item.product_id,
                quantity: item.quantity
              });
            });
            
            setGroupProductQuantities(quantitiesByGroup);
            console.log("QuickUsageTracker: Loaded group quantities:", quantitiesByGroup);
          }
        } catch (err) {
          console.error("Error loading group product quantities:", err);
          // Continue anyway, just log the error
        }
      }
      
      setError(false);
    } catch (error) {
      console.error("Error fetching data for quick usage tracker:", error);
      
      // Set error state
      setError(true);
      
      // Fallback to local storage
      console.log("Falling back to local storage data");
      setGroups(getGroups());
      setProducts(getProducts());
      
      if (isRetry) {
        toast({
          variant: "destructive",
          title: "Sync failed",
          description: "Some data could not be synced. Please try again later.",
        });
      }
    } finally {
      setIsLoading(false);
    }
  }, [toast]);
  
  // Initial data load
  useEffect(() => {
    loadData();
  }, [loadData]);
  
  const handleRetry = useCallback(() => {
    loadData(true);
  }, [loadData]);
  
  return {
    groups,
    products,
    isLoading,
    error,
    groupProductQuantities,
    handleRetry
  };
};

// Adding a default export to maintain compatibility
export default useUsageTrackerData;
